import * as React from 'react'
import {
  ALL_SORT_BLOCK_TYPES,
  AtLeast,
  Block,
  ListBlock,
  isComponentBlock,
  ListSort,
  sortDirectionsRecord,
} from '@basehub/data/replicache/index'
import { getId } from '@basehub/utils/id/index'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { invariant } from '@epic-web/invariant'
import {
  Cross1Icon,
  DragHandleDots2Icon,
  PlusIcon,
} from '@radix-ui/react-icons'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Button } from '~/sotano-kit/primitives/button'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { clsx } from 'clsx'
import { useSelectedView } from '../use-selected-view'
import { swapIndexes } from '../../helpers/sort'
import { BlockIcon } from '~/components/block-icon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '~/sotano-kit/primitives/dialog'
import { ToggleState } from '~/hooks/use-toggle-state'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { UpdateListViewParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/list'
import { useTabsStore } from '../../../../tabs/store'
import { useEditorMode } from '../../../../editor-mode-provider'
import {
  SortDirection,
  sortDirectionsEnum,
} from '@basehub/data/replicache/schema/block/search'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export const ListBlockSortPopover = ({
  block,
  path,
  toggleState,
  children,
}: {
  block: ListBlock
  parent: Block
  path: string
  toggleState: ToggleState
  children: React.ReactNode
}) => {
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const editorMode = useEditorMode()
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const [selectedView, updateSelectedView] = useSelectedView(block)
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const focusOpTable = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          type: 'block:wrapper',
          blockId: sourceBlockId,
        },
        preferredTabKey: activeTabKey,
      },
    } satisfies RepoUndoOps[number]
  }, [sourceBlockId, activeTabKey])

  const targetBlockId_withinTree =
    blockTreeManager?.getNode(block.typeOptions.targetBlockId)?.value.id ?? ''

  const possibleBlocksToSort = React.useMemo(() => {
    if (!blockTreeManager) return []

    const template = blockTreeManager.getNode(
      block.typeOptions.targetBlockId
    )?.value

    invariant(
      template,
      `List template could not be found: ${block.typeOptions.targetBlockId}`
    )
    invariant(
      isComponentBlock(template),
      'List template should be a component block'
    )

    const possibleBlocks: AtLeast<Block, 'id' | 'type' | 'title'>[] = [
      {
        id: '__title',
        type: 'text',
        title: 'Title',
      },
      {
        id: '__createdAt',
        type: 'date',
        title: 'Created At',
      },
      {
        id: '__updatedAt',
        type: 'date',
        title: 'Updated At',
      },
    ]

    blockTreeManager
      .getChildrenKeys(idToSourceBlockId(template))
      .forEach((childId) => {
        const childBlock = blockTreeManager?.getNode(childId)?.value
        if (!childBlock) return

        if (!ALL_SORT_BLOCK_TYPES.includes(childBlock.type)) {
          return
        }

        possibleBlocks.push(childBlock)
      })

    return possibleBlocks
  }, [block.typeOptions.targetBlockId, blockTreeManager])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  return (
    <Popover open={toggleState.isOn} onOpenChange={toggleState.handleToggle}>
      {children}

      <PopoverTrigger className="cursor-default" asChild>
        <span className="absolute opacity-0 h-full right-0" />
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="pl-4 pr-3 py-3 !bg-dark-base shadow-modal w-[520px] max-w-[90vw] outline-none"
        onInteractOutside={(e) => {
          const target = e.target
          if (!(target instanceof HTMLElement)) return
          if (target.role === `sort-trigger-${block.id}`) {
            e.preventDefault()
          }
        }}
      >
        <DndContext
          sensors={sensors}
          autoScroll={false}
          onDragEnd={async ({ active, over }) => {
            if (
              !blockTreeManager ||
              !selectedView ||
              editorMode !== 'write:all'
            ) {
              return
            }

            if (over && active.id !== over?.id) {
              const activeIndex = selectedView.sort.findIndex(
                ({ by }, index) => {
                  if (!active.data?.current) return false
                  return by + index === active.id
                }
              )
              const overIndex = selectedView.sort.findIndex(({ by }, index) => {
                if (!over.data?.current) return false
                return by + index === over.id
              })

              const reorderedValue = swapIndexes(
                [...selectedView.sort],
                activeIndex,
                overIndex
              )
              if (!reorderedValue) return

              const params: UpdateListViewParams = {
                blockId: block.id,
                targetBlockId_withinTree,
                id: selectedView?.id,
                data: { sort: reorderedValue },
                path,
                idSeed: getId(),
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateListView(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [focusOpTable, { op: 'updateListView', args: params }],
                  undo: [
                    focusOpTable,
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }
          }}
        >
          <SortableContext
            disabled={editorMode !== 'write:all'}
            strategy={verticalListSortingStrategy}
            items={(selectedView?.sort ?? []).map(
              ({ by }, index) => by + index
            )}
          >
            <p className="font-medium text-dark-muted mb-1 text-sm">Sort</p>
            <div className="space-y-3">
              {!selectedView?.sort.length ? (
                <p className="text-dark-dim text-sm h-7 leading-7">
                  No sorts applied to this view yet
                </p>
              ) : (
                selectedView.sort.map((sort, index) => {
                  return (
                    <SortMethod
                      key={sort.by + sort.type + index}
                      sort={sort}
                      index={index}
                      possibleBlocksToSort={possibleBlocksToSort}
                      onChangeSortBy={async (sortBy) => {
                        if (!replicache || editorMode !== 'write:all') return
                        if (sortBy === sort.by) return

                        const params: UpdateListViewParams = {
                          id: selectedView?.id,
                          blockId: block.id,
                          targetBlockId_withinTree,
                          path,
                          idSeed: getId(),
                          data: {
                            sort: (selectedView?.sort ?? []).map((sort, i) => {
                              if (i !== index) return sort

                              return { ...sort, by: sortBy }
                            }),
                          },
                          isoNow: new Date().toISOString(),
                        }
                        const result =
                          await replicache?.mutate.updateListView(params)
                        if (result?.undo) {
                          undoManager.current?.push({
                            redo: [
                              focusOpTable,
                              { op: 'updateListView', args: params },
                            ],
                            undo: [
                              focusOpTable,
                              {
                                op: 'blockBatchMutations',
                                args: { mutations: result.undo },
                              },
                            ],
                          })
                        }
                      }}
                      onChangeSortDirection={async (newType: SortDirection) => {
                        if (!replicache || editorMode !== 'write:all') return
                        const currentSortType = sort.type
                        if (newType === currentSortType) return

                        const params: UpdateListViewParams = {
                          id: selectedView.id,
                          blockId: block.id,
                          targetBlockId_withinTree,
                          path,
                          idSeed: getId(),
                          data: {
                            ...selectedView,
                            sort: selectedView.sort.map((sortMethod, i) => {
                              if (i !== index) return sortMethod
                              return { ...sortMethod, type: newType }
                            }),
                          },
                          isoNow: new Date().toISOString(),
                        }
                        const result =
                          await replicache?.mutate.updateListView(params)
                        if (result?.undo) {
                          undoManager.current?.push({
                            redo: [
                              focusOpTable,
                              { op: 'updateListView', args: params },
                            ],
                            undo: [
                              focusOpTable,
                              {
                                op: 'blockBatchMutations',
                                args: { mutations: result.undo },
                              },
                            ],
                          })
                        }
                      }}
                      onDeleteSortMethod={async () => {
                        // remove this sorting method
                        if (
                          !replicache ||
                          !selectedView ||
                          editorMode !== 'write:all'
                        ) {
                          return
                        }

                        const params: UpdateListViewParams = {
                          id: selectedView.id,
                          blockId: block.id,
                          targetBlockId_withinTree,
                          idSeed: getId(),
                          data: {
                            sort: selectedView.sort.filter(
                              (_, i) => i !== index
                            ),
                          },
                          isoNow: new Date().toISOString(),
                          path,
                        }
                        const result =
                          await replicache.mutate.updateListView(params)
                        if (result?.undo) {
                          undoManager.current?.push({
                            redo: [
                              focusOpTable,
                              { op: 'updateListView', args: params },
                            ],
                            undo: [
                              focusOpTable,
                              {
                                op: 'blockBatchMutations',
                                args: { mutations: result.undo },
                              },
                            ],
                          })
                        }
                      }}
                    />
                  )
                })
              )}
              {editorMode === 'write:all' && (
                <Button
                  unstyled
                  focusStyled
                  className="text-dark-muted text-xs mt-4 font-medium hover:text-dark-dim flex items-center gap-x-1 leading-none cursor-default"
                  icon={<PlusIcon width={16} height={16} />}
                  iconSide="left"
                  onClick={async () => {
                    if (!replicache || !blockTreeManager) return

                    const params: UpdateListViewParams = {
                      id: selectedView?.id,
                      idSeed: getId(),
                      blockId: block.id,
                      targetBlockId_withinTree,
                      data: {
                        sort: [
                          ...(selectedView?.sort ?? []),
                          {
                            by: '__title',
                            type: sortDirectionsEnum.Values['asc'],
                          },
                        ],
                      },
                      isoNow: new Date().toISOString(),
                      path,
                    }
                    const { view, undo } =
                      await replicache.mutate.updateListView(params)

                    if (view && selectedView?.id !== view.id) {
                      await updateSelectedView(view.id)
                    }

                    if (undo) {
                      undoManager.current?.push({
                        redo: [
                          focusOpTable,
                          { op: 'updateListView', args: params },
                        ],
                        undo: [
                          focusOpTable,
                          {
                            op: 'blockBatchMutations',
                            args: { mutations: undo },
                          },
                        ],
                      })
                    }
                  }}
                >
                  Pick a column to sort by
                </Button>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </PopoverContent>
    </Popover>
  )
}

const SortMethod = ({
  possibleBlocksToSort,
  sort,
  index, // sort by + sort direction + index in sort list
  onChangeSortBy,
  onChangeSortDirection,
  onDeleteSortMethod,
}: {
  possibleBlocksToSort: AtLeast<Block, 'id' | 'title' | 'type'>[]
  sort: ListSort
  index: number
  onChangeSortBy: (sortBy: string) => void
  onChangeSortDirection: (newType: SortDirection) => void
  onDeleteSortMethod: () => void
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({
      id: sort.by + index,
    })
  const translate = { transform: CSS.Transform.toString(transform) }
  const blockTreeManager = useWorkingTreeStore((state) => state.treeRef)
  const block = blockTreeManager.current?.getNode(sort.by)?.value
  const blockType = block?.type ?? 'text'
  const editorMode = useEditorMode()

  return (
    <div
      className="flex items-center gap-x-2"
      ref={setNodeRef}
      style={translate}
    >
      {editorMode === 'write:all' && (
        <DragHandleDots2Icon
          className={clsx(
            'cursor-grab text-dark-faint focus:outline-none focus:ring-none focus-visible:outline-dark-control-base focus-visible:outline-offset-1 focus-visible:outline-1 focus-within:outline shrink-0 rounded-sm',
            isDragging && 'cursor-grabbing outline'
          )}
          {...listeners}
          {...attributes}
        />
      )}
      <Select
        defaultValue={sort.by}
        onValueChange={onChangeSortBy}
        disabled={editorMode !== 'write:all'}
      >
        <SelectTrigger className="min-w-1/3 flex-grow shrink-0">
          <SelectValue />
          <BlockIcon
            block={{ type: blockType }}
            iconProps={{ width: 12, height: 12, className: 'shrink-0' }}
          />
        </SelectTrigger>
        <SelectContent>
          {possibleBlocksToSort.map((block) => {
            return (
              <SelectItem value={idToSourceBlockId(block)} key={block.id}>
                {block.title || 'Untitled'}
                {Boolean(block.id.startsWith('__')) && (
                  <span className="text-[11px] text-dark-muted">
                    &nbsp;(sys)
                  </span>
                )}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      <Select
        defaultValue={sort.type}
        onValueChange={onChangeSortDirection}
        disabled={editorMode !== 'write:all'}
      >
        <SelectTrigger className="min-w-1/3 flex-grow shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(sortDirectionsRecord).map(
            ([option, label], index) => {
              return (
                <SelectItem value={option} key={index}>
                  {label}
                </SelectItem>
              )
            }
          )}
        </SelectContent>
      </Select>

      {editorMode === 'write:all' && (
        <IconButton onClick={onDeleteSortMethod}>
          <Cross1Icon className="text-dark-faint" width={16} height={16} />
        </IconButton>
      )}
    </div>
  )
}

export const ConfirmSortDeletionModal = ({
  block,
  path,
  open,
  onOpenChange,
}: {
  block: ListBlock
  path: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const [selectedView] = useSelectedView(block)
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const targetBlockId_withinTree = useTreeSlice(blockTreeManager, (s) => {
    return s.getNode(block.typeOptions.targetBlockId)?.value.id ?? ''
  })

  return (
    <Dialog modal open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Would you like to remove sorting?</DialogTitle>
        <DialogFooter className="mt-8" divider>
          <DialogClose asChild>
            <Button intent="secondary">Cancel</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button
              intent="primary"
              onClick={async () => {
                if (!selectedView || !replicache) return

                await replicache?.mutate.updateListView({
                  blockId: block.id,
                  targetBlockId_withinTree: targetBlockId_withinTree ?? '',
                  id: selectedView.id,
                  idSeed: getId(),
                  data: { sort: [] },
                  isoNow: new Date().toISOString(),
                  path,
                })
              }}
            >
              Remove
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const ArrowUpDownIcon = ({
  width,
  height,
  className = '',
}: {
  width: number | string
  height: number | string
  className: string
}) => {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      fill="none"
      viewBox="0 0 18 18"
    >
      <path
        fill="currentColor"
        d="M8.273 11.977a.563.563 0 0 1 0 .796l-2.25 2.25a.562.562 0 0 1-.796 0l-2.25-2.25a.563.563 0 0 1 .796-.796l1.29 1.29V3.376a.562.562 0 1 1 1.125 0v9.893l1.289-1.29a.562.562 0 0 1 .796 0Zm6.75-6.75-2.25-2.25a.563.563 0 0 0-.796 0l-2.25 2.25a.563.563 0 1 0 .796.796l1.29-1.29v9.892a.563.563 0 0 0 1.124 0V4.733l1.29 1.29a.563.563 0 0 0 .796-.796Z"
      />
    </svg>
  )
}
